import { useState, useEffect } from "react";
import { getUserId } from "../../config";
import firebase from "firebase/app";
import "firebase/storage";
import firestore from "src/apiService/firestore";
import { getCallableErrorMessage } from "src/utils/callableErrors";

const db = firestore();

export type PlaidIntegrationType = "expense";
export interface PlaidLinkData {
  type: PlaidIntegrationType;
  name: string;
  date: Date;
  redirect?: string;
  bankAccountId?: string;
  bankAccountType?: string;
}
export interface PlaidLinkBag {
  linkToken?: string;
  data?: PlaidLinkData;
  error?: string;
}

async function fetchPlaidLinkToken() {
  const f = firebase.functions().httpsCallable("getPlaidLinkToken");
  const result = await f();
  return result.data;
}

export const PlaidLinkKey = "__MRG_PlaidLinkKey__";
function getStoredLinkData(): PlaidLinkBag {
  try {
    const stored = window.localStorage.getItem(PlaidLinkKey);
    const json = JSON.parse(stored || "{}");
    if (json.data?.date) {
      json.data.date = new Date(json.data.date);
      json.data.date.setHours(0, 0, 0, 0);
    }
    return json;
  } catch (e) {
    return { error: (e as Error).toString() };
  }
}

function updateStoredLinkData(bag: PlaidLinkBag) {
  let mergedBag: PlaidLinkBag = {};
  const stored = getStoredLinkData();
  if (!stored.error) mergedBag = stored;
  window.localStorage.setItem(
    PlaidLinkKey,
    JSON.stringify({
      ...mergedBag,
      ...bag,
    })
  );
}

export function usePlaid(data?: Partial<PlaidLinkData>): PlaidLinkBag {
  const [link, setLink] = useState<PlaidLinkBag>({});

  useEffect(() => {
    if (window.location.pathname.endsWith("/auth/plaid/callback")) {
      setLink(getStoredLinkData());
      return;
    }

    let cancel = false;
    (async () => {
      try {
        const linkToken = await fetchPlaidLinkToken();
        if (cancel) return;
        setLink((l) => ({ ...l, linkToken, error: undefined }));
        updateStoredLinkData({ linkToken });
      } catch (error) {
        if (cancel) return;
        const message = getCallableErrorMessage(error);
        setLink((l) => ({ ...l, error: message }));
      }
    })();

    return () => {
      cancel = true;
    };
  }, []);

  // XXX: this isn't the best solution, but, well, it works.
  //      should return a setter instead.
  useEffect(() => {
    if (data?.type && data?.name && data?.date) {
      updateStoredLinkData({
        data: {
          type: data.type,
          name: data.name,
          date: data.date,
          redirect: data.redirect,
        },
      });
    }
  }, [data?.type, data?.name, data?.date, data?.redirect]);

  return link;
}

export async function fetchPlaidUpdateModeLinkToken(integrationId: string) {
  const getPlaidUpdateModeLinkFunction = firebase
    .functions()
    .httpsCallable("getPlaidUpdateModeLinkToken", { timeout: 10000 });
  const result = await getPlaidUpdateModeLinkFunction({ integrationId });
  return result.data;
}

export async function transferPlaidPublicToken(
  public_token: string,
  type: PlaidIntegrationType | null,
  extra:
    | { integration_id?: string }
    | {
        name: string;
        date?: Date;
        bankAccountId: string;
        bankAccountType: string;
      }
) {
  const f = firebase.functions().httpsCallable("exchangePlaidPublicToken");
  const data = await f({
    ...extra,
    public_token: public_token,
    type,
  });
  return data.data;
}

export const deleteIntegration = async (integrationId) => {
  const deleteIntegration = firebase
    .functions()
    .httpsCallable("deletePlaidIntegration");
  try {
    const data = await deleteIntegration({ integration_id: integrationId });
    return data.data;
  } catch (err) {
    console.log(err);
    return "";
  }
};

export const deleteTransactions = () => {
  return getUserId().then(async (userId) => {
    const ref = db.collection("Transactions").where("user", "==", userId);
    ref.get().then(function (querySnapshot) {
      querySnapshot.forEach(function (doc) {
        doc.ref.delete();
      });
    });
  });
};

export async function syncAccount(doc_id) {
  const f = firebase.functions().httpsCallable("getPlaidTransactions");
  return (await f({ doc_id: doc_id })).data;
}
